import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/* ── i18n labels ── */
const L = {
  es: {
    selected: (n) => `${n} tarjeta${n !== 1 ? 's' : ''} seleccionada${n !== 1 ? 's' : ''}`,
    clear: 'Limpiar',
    compare: 'Comparar',
    compareNow: 'Comparar ahora',
    trayLabel: 'Bandeja de comparación',
  },
  en: {
    selected: (n) => `${n} card${n !== 1 ? 's' : ''} selected`,
    clear: 'Clear',
    compare: 'Compare',
    compareNow: 'Compare now',
    trayLabel: 'Comparison tray',
  },
  fr: {
    selected: (n) => `${n} carte${n !== 1 ? 's' : ''} sélectionnée${n !== 1 ? 's' : ''}`,
    clear: 'Effacer',
    compare: 'Comparer',
    compareNow: 'Comparer maintenant',
    trayLabel: 'Barre de comparaison',
  },
};

const CompareTray = ({ selectedCards, onRemove, onClear, onCompare }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { i18n } = useTranslation();
  const lang = i18n.language || 'es';
  const l = L[lang] || L.es;
  const count = selectedCards.length;

  return (
    <Slide direction="up" in={count >= 1} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1299,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
          px: { xs: 2, md: 4 },
          py: 1.5,
        }}
        role="region"
        aria-label={l.trayLabel}
      >
        <Box
          sx={{
            maxWidth: 'lg',
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {isMobile ? (
            /* Mobile: compact */
            <>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {l.selected(count)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={onClear}
                  sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0 }}
                >
                  {l.clear}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={count < 2}
                  onClick={onCompare}
                  sx={{
                    bgcolor: 'secondary.main',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    '&:hover': { bgcolor: 'secondary.dark' },
                  }}
                >
                  {l.compare} ({count})
                </Button>
              </Box>
            </>
          ) : (
            /* Desktop: full */
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>
                  {l.compare} ({count}/4):
                </Typography>
                {selectedCards.map((c) => (
                  <Chip
                    key={c.id}
                    label={c.name}
                    size="small"
                    onDelete={() => onRemove(c)}
                    deleteIcon={<CloseIcon sx={{ fontSize: '0.9rem' }} />}
                    sx={{
                      fontWeight: 500,
                      maxWidth: 180,
                      '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                <Button
                  size="small"
                  onClick={onClear}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  {l.clear}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={count < 2}
                  onClick={onCompare}
                  sx={{
                    bgcolor: 'secondary.main',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 3,
                    '&:hover': { bgcolor: 'secondary.dark' },
                  }}
                >
                  {l.compareNow}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Slide>
  );
};

export default CompareTray;
